import React from 'react';
import { CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceHero from '../components/ServiceHero';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const ShrinkWrapping = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    "Patio furniture, sectionals, umbrellas, and cushions",
    "Grills, fire pits, and seasonal outdoor equipment",
    "Boats, jet skis, and recreational gear",
    "Pool heaters, yard machinery, and storage items",
    "Commercial and construction equipment"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <ServiceHero
        title="Shrink Wrapping"
        headline="Winter Coming?"
        description="Protect your outdoor furniture and equipment from salt air, moisture, and winter weather with our marine-grade shrink wrapping services."
        backgroundImage="https://customer-assets.emergentagent.com/job_clean-hamptons-demo/artifacts/op2yw6sf_Shrink%20Wrapping%206.png"
        defaultService="Shrink Wrapping (Outdoor Furniture, etc)"
      />
      
      {/* Content Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-6">
                Our Shrink Wrapping Services
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Each winter season, outdoor furniture, and equipment face the same threats:{' '}
                <strong>salt air, sandy winds, moisture, and winter weather</strong>. Left uncovered, these 
                elements <strong>can cause corrosion, mildew, and costly damage</strong>.
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                That's why property owners across The Hamptons trust{' '}
                <span className="text-gradient-reflections font-bold">Reflections Cleanings</span> shrink wrapping services. Our durable, marine-grade shrink wrap creates a{' '}
                <strong>tight, weatherproof seal</strong> that protects your valuables all season long.
              </p>

              <p className="text-gray-700 font-medium mb-4">
                We shrink wrap:
              </p>

              <ul className="space-y-3 mb-8">
                {services.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{service}</span>
                  </li>
                ))}
              </ul>

              <div className="mb-8">
                <p className="font-semibold text-gray-900 mb-2">We Also Offer:</p>
                <p className="text-gray-700">
                  Beyond Shrink Wrapping, our team is trusted across Long Island for{' '}
                  <strong>Window Cleaning, House Washing, Roof Washing, and Gutter Cleaning</strong>.{' '}
                  <Link to="/#services" className="text-blue-600 hover:text-blue-700 font-medium">
                    Explore all our services <ArrowRight className="inline w-4 h-4" />
                  </Link>
                </p>
              </div>

              <button
                onClick={scrollToQuote}
                data-testid="shrink-wrapping-cta"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Request a Quote
              </button>
              <p className="mt-3 text-sm text-gray-600">
                Get within 1 business day
              </p>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="grid grid-cols-1 gap-6">
                {/* Main Image - Shrink Wrapping */}
                <div className="relative">
                  <img
                    src="/images/shrink-wrap/shrink-wrap-new.jpg"
                    alt="Professional shrink wrapping service for outdoor furniture"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Top Right */}
                  <div className="absolute -top-4 -right-4 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8" />
                      <div>
                        <div className="text-2xl font-bold">15+ Years</div>
                        <div className="text-sm text-blue-100">Of Experience</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Image - Shrink Wrapping */}
                <div className="relative">
                  <img
                    src="/images/shrink-wrap/shrink-wrap-2.jpg"
                    alt="Shrink wrapped outdoor furniture and equipment"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Bottom Left */}
                  <div className="absolute -bottom-4 -left-4 bg-white text-gray-900 px-6 py-4 rounded-2xl shadow-lg border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-xl">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">1450+ Clients</div>
                        <div className="text-sm text-gray-600">Satisfied By Us</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to Protect Your Valuables?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get a free, no-obligation quote within 1 business day. 
            Our shrink wrapping experts are ready to keep your outdoor items safe all winter.
          </p>
          <Link
            to="/#quote"
            data-testid="shrink-wrapping-quote-link"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default ShrinkWrapping;
